import { isAlias, isMap, isScalar, isSeq, parseDocument } from "yaml";

// Microsoft authoring schema, pinned 1838950fa95f1d2f8365e826acd8a600ff593bd9:
// https://raw.githubusercontent.com/microsoft/skills-for-copilot-studio/1838950fa95f1d2f8365e826acd8a600ff593bd9/reference/bot.schema.yaml-authoring.json
// ModelContextProtocolMetadata -> McpToolDefinitionNoKind -> RecordNoKind /
// PropertyInfoNoKind permit descriptive schemas, not per-tool default bindings.
// This is narrow advisory lint, not a complete schema or runtime validator.
const MAX_SOURCE = 500_000;
const MAX_TOOLS = 100;
const MAX_PROPERTY_NODES = 1_000;
const MAX_DEPTH = 8;
const BINDING_KEYS = [
  "default",
  "defaults",
  "defaultValue",
  "defaultValues",
  "value",
  "inputDefaults",
  "toolDefaults",
  "toolInputs",
  "inputs",
  "inputBindings",
  "bindings",
] as const;
const DETAILS_DIAGNOSTIC =
  "MCP operationDetails contains unsupported default/input-binding fields. The verified authoring schema does not define per-discovered-tool defaults here; saved source is not evidence of enforced input values.";
const TOOL_DIAGNOSTIC =
  "MCP knownTools contains unsupported default/input-binding fields. Its inputSchema is descriptive metadata, not a verified per-tool default binding; saved source is not evidence of enforced input values.";
const PROPERTY_DIAGNOSTIC =
  "MCP knownTools.inputSchema contains unsupported default/value/input-binding fields in its schema metadata. Examples and type metadata must not be treated as enforced runtime defaults.";
const INCOMPLETE_DIAGNOSTIC =
  "MCP per-tool default preflight is incomplete because source, shape, aliases or traversal exceeded its supported bounds. No default binding or runtime enforcement was verified.";

/** Fixed diagnostics only: never returns source keys, parameter names or values. */
export function lintMcpToolInputDefaults(source: string): string[] {
  if (source.length > MAX_SOURCE) {
    return source.includes("ModelContextProtocolMetadata") ? [INCOMPLETE_DIAGNOSTIC] : [];
  }
  try {
    const document = parseDocument(source, { uniqueKeys: true, strict: true });
    if (document.errors.length !== 0 || !isMap(document.contents)) return [];
    const root = document.contents;
    const rootKind: unknown = root.get("kind", true);
    const action: unknown = root.get("action", true);
    if (!isScalar(rootKind) || rootKind.value !== "TaskDialog" || !isMap(action)) return [];
    const actionKind: unknown = action.get("kind", true);
    const details: unknown = action.get("operationDetails", true);
    if (
      !isScalar(actionKind) ||
      actionKind.value !== "InvokeExternalAgentTaskAction" ||
      !isMap(details)
    )
      return [];
    const detailsKind: unknown = details.get("kind", true);
    if (!isScalar(detailsKind) || detailsKind.value !== "ModelContextProtocolMetadata") return [];

    const diagnostics = new Set<string>();
    const hasBindingKeys = (node: unknown): boolean =>
      isMap(node) && BINDING_KEYS.some((key) => node.has(key));
    if (hasBindingKeys(details)) diagnostics.add(DETAILS_DIAGNOSTIC);

    const knownTools: unknown = details.get("knownTools", true);
    if (knownTools === undefined) return [...diagnostics];
    if (!isSeq(knownTools) || knownTools.items.length > MAX_TOOLS) {
      diagnostics.add(INCOMPLETE_DIAGNOSTIC);
      return [...diagnostics];
    }

    let visited = 0;
    const inspectRecord = (record: unknown, depth: number): void => {
      if (depth > MAX_DEPTH || ++visited > MAX_PROPERTY_NODES || !isMap(record)) {
        diagnostics.add(INCOMPLETE_DIAGNOSTIC);
        return;
      }
      if (hasBindingKeys(record)) diagnostics.add(PROPERTY_DIAGNOSTIC);
      const properties: unknown = record.get("properties", true);
      if (properties === undefined) return;
      if (!isMap(properties) || properties.items.length > MAX_PROPERTY_NODES) {
        diagnostics.add(INCOMPLETE_DIAGNOSTIC);
        return;
      }
      for (const property of properties.items) {
        if (++visited > MAX_PROPERTY_NODES) {
          diagnostics.add(INCOMPLETE_DIAGNOSTIC);
          return;
        }
        const metadata: unknown = property.value;
        if (isAlias(metadata)) {
          diagnostics.add(INCOMPLETE_DIAGNOSTIC);
          continue;
        }
        // PropertyInfo may be a scalar built-in type. Property *names* may
        // legitimately be "default" or "value"; only inspect their metadata.
        if (!isMap(metadata)) continue;
        if (hasBindingKeys(metadata)) diagnostics.add(PROPERTY_DIAGNOSTIC);
        const type: unknown = metadata.get("type", true);
        if (isAlias(type)) diagnostics.add(INCOMPLETE_DIAGNOSTIC);
        const nested = isMap(type) ? type : metadata;
        const kind: unknown = nested.get("kind", true);
        if (isScalar(kind) && (kind.value === "Record" || kind.value === "Table")) {
          inspectRecord(nested, depth + 1);
        }
      }
    };
    for (const tool of knownTools.items) {
      if (!isMap(tool)) {
        diagnostics.add(INCOMPLETE_DIAGNOSTIC);
        continue;
      }
      if (hasBindingKeys(tool)) diagnostics.add(TOOL_DIAGNOSTIC);
      const inputSchema: unknown = tool.get("inputSchema", true);
      if (inputSchema !== undefined) inspectRecord(inputSchema, 0);
    }
    return [...diagnostics];
  } catch {
    // The existing linter owns general YAML diagnostics. Do not echo parser
    // exceptions or broaden this targeted check into a new source validator.
    return source.includes("ModelContextProtocolMetadata") ? [INCOMPLETE_DIAGNOSTIC] : [];
  }
}
